import type { Scope } from "@69ly/shared";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { PERSONAL } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useTeams } from "../lib/queries.ts";
import { useCopyStuffs } from "../lib/stuff-queries.ts";
import { Icon } from "./Icon.tsx";

/** The libraries a player can copy into: their teams and their personal library, minus `from`. */
export function useDestinations(from: Scope) {
  const { t } = useTranslation();
  const teams = useTeams();
  return [...(teams.data ?? []).map((x) => ({ scope: x.id as Scope, name: x.name })), { scope: PERSONAL as Scope, name: t("nav.personal") }].filter(
    (d) => d.scope !== from,
  );
}

/** "Copy to…" on a stuff's page: pick a library, the copy goes there. */
export function CopyToMenu({ stuffId, from, small }: { stuffId: string; from: Scope; small?: boolean }) {
  const { t } = useTranslation();
  const destinations = useDestinations(from);
  const copy = useCopyStuffs();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<{ name: string; id: string | null } | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  if (destinations.length === 0) return null;
  return (
    <div className="copy-to" ref={box}>
      <button type="button" className={`btn btn-secondary${small ? " btn-small" : ""}`} aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen(!open)} disabled={copy.isPending}>
        <Icon name="copy" size={small ? 14 : 16} />
        {copy.isPending ? t("copy.copying") : t("copy.to")}
      </button>
      {open && (
        <div className="copy-menu" role="menu">
          {destinations.map((d) => (
            <button
              key={d.scope}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setDone(null);
                copy.mutate({ ids: [stuffId], to: d.scope }, { onSuccess: (r) => setDone({ name: d.name, id: r.copied[0]?.id ?? null }) });
              }}
            >
              {d.name}
            </button>
          ))}
        </div>
      )}
      {done && (
        <p className="copy-result small" role="status">
          {done.id ? (
            <>
              {t("copy.done", { name: done.name })} <Link to={`/stuffs/${done.id}`}>{t("copy.open")}</Link>
            </>
          ) : (
            t("copy.already", { name: done.name })
          )}
        </p>
      )}
      {copy.error && <p className="field-error small">{errorText(t, copy.error)}</p>}
    </div>
  );
}
